import { location, onMount, reactive } from 'cdui-js';
import { Show } from 'solid-js';
import { replaceTemplate } from '@china-difi/lib/template';
import i18n from '../../../i18n';
import { resultFailIcon as removeIcon } from '../../../assets/commonIcons';
import {
  fetchStockTransferInSettlement,
  fetchStockTransferOutBrokers,
  saveStockTransferInApply,
  type StockTransferInSettlement,
  type StockTransferOutBroker,
} from '../../../api/stockTransferIn';
import { DiFiInput } from '../../../components/DiFi/DiFiInput';
import { AuthSensitiveImage } from '../components/AuthSensitiveImage';
import { PROOF_IMAGE_MAX_BYTES, UploadImage } from '../components/Upload';
import { showToast, showToastError } from '../components/Toast';
import { goBackByBrowser, routeReplace } from '../../../lib/browser';
import { bootDepositApp } from '../deposit/h5-deposit/appBridge';
import { awaitUserInfoReady, userState } from '../../../stores/user';
import { IconUploadPlus } from '../deposit/h5-deposit/icons';
import { BrokerMark } from './BrokerMark';
import { StiFormField } from './FormField';
import { StockTransferNav } from './Nav';
import {
  brokerDisplayName,
  buildInSettlementView,
  buildStockTransferApplySaveBody,
  defaultInAccountTypeCode,
  displayInAccountTypeLabel,
  parseStockTransferApplyId,
  setStockTransferApplyId,
  fixedInboundBrokerName,
  brokerIdEquals,
  getOtherOutBrokerDraft,
  getStockTransferDraft,
  getSelectedOutBroker,
  INBOUND_BROKER_LOGO,
  resolveInboundCcassId,
  readBrokerIdQuery,
  readMarketQuery,
  setStockTransferDraft,
  statementProofKindFromPath,
  stockTransferUserAccountFullName,
  syncStockTransferAppShell,
  withStockTransferQuery,
  type StatementProof,
  type StatementProofKind,
  type StockTransferMarket,
} from './shared';
import './page.css';

const MAX_STATEMENTS = 10;

const filePathOf = (data: unknown) => {
  const row = data as Record<string, unknown>;
  if (typeof row?.filePath === 'string') return row.filePath.trim();
  const nested = row?.data as Record<string, unknown> | undefined;
  if (typeof nested === 'string') return nested.trim();
  if (typeof nested?.filePath === 'string') return nested.filePath.trim();
  return '';
};

const FlowArrowBadge = () => (
  <div class="sti-broker-arrow-badge" aria-hidden="true">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 5V19M12 19L7 14M12 19L17 14"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </div>
);

export const StockTransferInBroker = () => {
  const state = reactive({
    market: 'HK' as StockTransferMarket,
    loading: true,
    uploading: false,
    saving: false,
    settlement: null as StockTransferInSettlement | null,
    broker: null as StockTransferOutBroker | null,
    accountNo: '',
    accountName: '',
    contactPerson: '',
    contactPhone: '',
    customName: '',
    userName: '',
    statements: [] as StatementProof[],
  });

  const isOther = () => state.broker?.isOther === 1;

  const outBrokerName = () => {
    const broker = state.broker;
    if (!broker) return '--';
    if (isOther() && state.customName.trim()) return state.customName.trim();
    return brokerDisplayName(broker);
  };

  const inSettlement = () => buildInSettlementView(state.settlement, state.market);

  const applyBroker = (broker: StockTransferOutBroker) => {
    state.broker = broker;
    state.contactPerson = broker.contactPerson || '';
    state.contactPhone = broker.contactPhone || '';
    if (broker.isOther === 1) {
      const draft = getOtherOutBrokerDraft();
      state.customName = draft?.customName || '';
      state.accountNo = '';
    } else {
      state.accountNo = broker.accountNo || '';
      state.customName = '';
    }
  };

  const redirectOtherBrokerStep = () => {
    const id = state.broker?.id ?? readBrokerIdQuery();
    location.routeTo(
      withStockTransferQuery('/h5/stock-transfer-in/other-broker', {
        market: state.market,
        brokerId: String(id),
      }),
      [0, 0],
    );
  };

  const resolveBroker = async (brokerId: number) => {
    const cached = getSelectedOutBroker();
    if (cached && brokerIdEquals(cached.id, brokerId)) {
      applyBroker(cached);
      return;
    }
    const list = await fetchStockTransferOutBrokers();
    const found = list.find((item) => brokerIdEquals(item.id, brokerId));
    if (found) {
      applyBroker(found);
      return;
    }
    location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/select-broker', { market: state.market }), [0, 0]);
  };

  const onUploadChange = (data: Record<string, unknown>) => {
    if (data.status === 'loading') {
      state.uploading = true;
      return;
    }
    state.uploading = false;
    if (data.status === 'error') return;
    const filePath = filePathOf(data);
    if (!filePath) {
      showToast(i18n.StockTransferIn.LoadFail);
      return;
    }
    const localUrl = typeof data.localPreviewUrl === 'string' ? data.localPreviewUrl : '';
    const kind: StatementProofKind =
      data.fileKind === 'pdf' || data.fileKind === 'image'
        ? data.fileKind
        : statementProofKindFromPath(filePath);
    state.statements = state.statements.concat([{ localUrl, filePath, kind }]);
  };

  const removeStatement = (index: number) => {
    const item = state.statements[index];
    if (item?.localUrl) URL.revokeObjectURL(item.localUrl);
    state.statements = state.statements.filter((_, i) => i !== index);
  };

  const onNext = async () => {
    if (state.saving) return;
    const broker = state.broker;
    if (!broker?.id) {
      showToast(i18n.StockTransferIn.PleaseSelectBroker);
      return;
    }
    if (broker.isOther === 1 && !state.customName.trim()) {
      showToast(i18n.StockTransferIn.PleaseInputOutParty);
      redirectOtherBrokerStep();
      return;
    }
    if (!state.accountNo.trim()) {
      showToast(i18n.StockTransferIn.PleaseInputAccountNoRequired);
      return;
    }
    // 暂时隐藏「转出券商账户名称」表单项，账户名称仍从登录用户姓名带出
    // if (!state.accountName.trim()) {
    //   showToast(i18n.StockTransferIn.PleaseInputOutBrokerAccountName);
    //   return;
    // }
    if (!state.statements.length) {
      showToast(i18n.StockTransferIn.UploadStatementRequiredToast);
      return;
    }
    const inbound = inSettlement();
    const inAccountNum =
      String(userState.accountNumHK || '').trim() || (inbound.accountNo !== '--' ? inbound.accountNo.trim() : '');
    const prev = getStockTransferDraft();
    const nextDraft = {
      applyId: prev?.applyId,
      marketCode: state.market,
      outBrokerId: broker.id,
      outBrokerName: outBrokerName(),
      outBrokerIsOther: broker.isOther === 1,
      outBrokerAccountNo: state.accountNo.trim(),
      outBrokerAccountName: state.userName === '--' ? '' : state.userName.trim(),
      outBrokerContactPerson: state.contactPerson.trim(),
      outBrokerContactPhone: state.contactPhone.trim(),
      outBrokerCatalogContactPhone: String(broker.contactPhone || '').trim(),
      outBrokerCustomName: state.customName.trim(),
      inBrokerName: inbound.brokerName,
      inCcassId:
        state.market === 'HK'
          ? resolveInboundCcassId(state.market, state.settlement)
          : inbound.ccassId !== '--'
            ? inbound.ccassId
            : '',
      inDtcNo: inbound.dtcNo !== '--' ? inbound.dtcNo : '',
      inAccountType: displayInAccountTypeLabel(defaultInAccountTypeCode(state.market), state.market),
      inAccountTypeCode: defaultInAccountTypeCode(state.market),
      inAccountNum,
      settlementAccountNo: inAccountNum || (inbound.accountNo !== '--' ? inbound.accountNo : ''),
      statementProofs: state.statements.map((item) => item.filePath),
      stocks: prev?.stocks ?? [],
    };
    const saveBody = buildStockTransferApplySaveBody(nextDraft);
    if (!saveBody) {
      showToast(i18n.StockTransferIn.LoadFail);
      return;
    }

    state.saving = true;
    try {
      try {
        await bootDepositApp();
      } catch {
        // 浏览器调试
      }
      const resp = await saveStockTransferInApply(saveBody);
      const applyId = parseStockTransferApplyId(resp);
      if (!applyId) {
        showToast(i18n.StockTransferIn.LoadFail);
        return;
      }
      setStockTransferApplyId(applyId);
      setStockTransferDraft({ ...nextDraft, applyId });
      location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/stocks', { market: state.market }), [0, 0]);
    } catch (e) {
      showToastError(e);
    } finally {
      state.saving = false;
    }
  };

  const onClose = () => {
    routeReplace(withStockTransferQuery('/h5/stock-transfer-in'));
  };

  onMount(() => {
    syncStockTransferAppShell();
    state.market = readMarketQuery();
    const brokerId = readBrokerIdQuery();
    if (!brokerId) {
      location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/select-broker', { market: state.market }), [0, 0]);
      return;
    }
    void (async () => {
      try {
        try {
          await bootDepositApp();
        } catch {
          // 浏览器调试继续用本地 token
        }
        try {
          await awaitUserInfoReady();
        } catch {
          // 姓名缺失时提示里用 --
        }
        state.userName = stockTransferUserAccountFullName() || '--';
        const savedDraft = getStockTransferDraft();
        await resolveBroker(brokerId);
        if (!state.broker) return;
        const sameBroker =
          !!savedDraft && String(savedDraft.outBrokerId) === String(state.broker.id);
        if (sameBroker && savedDraft) {
          state.accountNo = String(savedDraft.outBrokerAccountNo || '').trim();
          state.contactPerson = String(savedDraft.outBrokerContactPerson || '').trim();
          state.contactPhone = String(savedDraft.outBrokerContactPhone || '').trim();
          const customName = String(savedDraft.outBrokerCustomName || '').trim() || getOtherOutBrokerDraft()?.customName || '';
          if (customName) state.customName = customName;
          state.statements = (savedDraft.statementProofs || [])
            .map((filePath) => String(filePath || '').trim())
            .filter(Boolean)
            .map((filePath) => ({
              filePath,
              localUrl: '',
              kind: statementProofKindFromPath(filePath),
            }));
        }
        if (state.broker.isOther === 1 && !state.customName.trim()) {
          redirectOtherBrokerStep();
          return;
        }
        state.settlement = await fetchStockTransferInSettlement(state.market);
      } catch {
        showToast(i18n.StockTransferIn.LoadFail);
      } finally {
        state.loading = false;
      }
    })();
  });

  return (
    <div class="stock-transfer-in-h5">
      <StockTransferNav title={i18n.StockTransferIn.FillOutBrokerTitle} onBack={goBackByBrowser} onClose={onClose} />
      <div class="sti-scroll">
        <div class="sti-tip" style={{ 'min-height': '64px' }}>
          {replaceTemplate(i18n.StockTransferIn.OutBrokerTip, {
            broker: outBrokerName(),
            name: state.userName || '--',
          })}
        </div>
        <div class="sti-broker-stack">
          <div class="sti-broker-panel sti-broker-panel-out sti-broker-panel-readonly">
            <BrokerMark
              large
              name={outBrokerName()}
              logo={state.broker ? String(state.broker.logo || '') : ''}
            />
            <div class="sti-broker-name">{outBrokerName()}</div>
            {/* 挂在上卡底边，随卡片高度自适应，不依赖固定 top */}
            <FlowArrowBadge />
          </div>
          <div class="sti-broker-panel sti-broker-panel-in">
            <BrokerMark large name={fixedInboundBrokerName()} logo={INBOUND_BROKER_LOGO} />
            <div class="sti-broker-name">{fixedInboundBrokerName()}</div>
          </div>
        </div>
        <Show when={!state.loading}>
        <div>
        <div class="sti-form">
          <StiFormField label={i18n.StockTransferIn.OutBrokerAccountNoRequired}>
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={64}
              placeholder={i18n.StockTransferIn.AccountNoPlaceholder}
              value={state.accountNo}
              onUpdateValue={(v) => {
                state.accountNo = v;
              }}
            />
          </StiFormField>
          {/* 暂时隐藏：转出券商账户名称（必填）
          <StiFormField label={i18n.StockTransferIn.OutBrokerAccountNameRequired}>
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={100}
              placeholder={i18n.StockTransferIn.OutBrokerAccountNamePlaceholder}
              value={state.accountName}
              onUpdateValue={(v) => {
                state.accountName = v;
              }}
            />
          </StiFormField>
          */}
          <StiFormField label={i18n.StockTransferIn.OptionalContactPerson}>
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={64}
              placeholder={i18n.StockTransferIn.ContactPersonPlaceholder}
              value={state.contactPerson}
              onUpdateValue={(v) => {
                state.contactPerson = v;
              }}
            />
          </StiFormField>
          <StiFormField label={i18n.StockTransferIn.OptionalContactPhone}>
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={32}
              inputMode="tel"
              placeholder={i18n.StockTransferIn.ContactPhonePlaceholder}
              value={state.contactPhone}
              onUpdateValue={(v) => {
                state.contactPhone = v;
              }}
            />
          </StiFormField>
        </div>
        <div class="sti-upload-section">
          <div class="sti-upload-title">{i18n.StockTransferIn.UploadStatementRequired}</div>
          <div class="sti-upload-hint">{i18n.StockTransferIn.UploadStatementHint}</div>
          <div class="sti-upload">
            {state.statements.map((item, index) => (
              <div class="sti-upload-slot">
                {item.kind === 'pdf' ? (
                  <div class="sti-upload-pdf-thumb" aria-hidden="true">
                    <span class="sti-upload-pdf-label">PDF</span>
                  </div>
                ) : item.localUrl ? (
                  <img src={item.localUrl} alt="" />
                ) : (
                  <AuthSensitiveImage class="sti-upload-thumb" path={item.filePath} enablePreview={false} />
                )}
                <button
                  type="button"
                  class="sti-upload-remove"
                  onclick={(event) => {
                    event.stopPropagation?.();
                    removeStatement(index);
                  }}
                >
                  <img src={removeIcon} alt="" />
                </button>
              </div>
            ))}
            {state.statements.length < MAX_STATEMENTS ? (
              <div class="sti-upload-slot sti-upload-add">
                <IconUploadPlus class="sti-upload-add-icon" />
                <UploadImage
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    'z-index': 2,
                  }}
                  useFor="stock_transfer_in_statement"
                  busId={String(userState.id || '0')}
                  restrictProofType={true}
                  maxSize={PROOF_IMAGE_MAX_BYTES}
                  compressBeforeUpload={true}
                  change={onUploadChange}
                />
              </div>
            ) : null}
          </div>
        </div>
        </div>
        </Show>
      </div>
      <div class="sti-footer">
        <button
          type="button"
          class="primary-btn btn-rounded width-100"
          disabled={state.loading || state.uploading || state.saving}
          onclick={onNext}
        >
          {state.saving ? i18n.StockTransferIn.Submitting : i18n.StockTransferIn.NextStep}
        </button>
      </div>
    </div>
  );
};
